import type { Asset } from '../core/asset/Container';
import type { Slide } from '../core/slide/Slide';
import type { TextAsset } from '..';
import { Config } from './config';

type Bounds = {
    x: number;
    y: number;
    width: number;
    height: number;
};

type VisibleAssetMetadata = {
    i?: number;
    name: string;
    classType: string;
    bounds: Bounds;
    attributes: { [k: string]: unknown };
};

type Node = {
    asset: VisibleAssetMetadata;
    children: Node[];
};

const LOREM_IPSUM = 'Lorem ipsum dolor sit amet';
const SUBSCENE_SEPARATOR = '||';

/*
The following section is concerned with retrieving the visible assets in a Luminary scene
*/
function getBounds(asset: Asset, screenWidth: number, screenHeight: number): Bounds {
    /**
     * We're running this in the Video Data Server (a node environment) where we don't have a <Canvas> component. So
     * we cannot use `getBounds()` as it's just 0 without loading content which isn't possible in Node.
     * Instead we use `approximateMaxBounds` which is pretty accurate but still an approximation of the values.
     * It is a tiny bit dependent on content (it ignores empty text assets) but otherwise should be stable regardless
     * of the content passed in.
     */
    const maxBounds = asset.approximateMaxBounds;
    const bounds = (() => {
        // Handle Lottie assets that might not have proper bounds calculated yet
        // Use the asset's actual maxWidth/maxHeight instead of approximateMaxBounds
        if (
            Config.isLottieAsset(asset) &&
            (parseInt(maxBounds.width.toFixed()) <= 1 || parseInt(maxBounds.height.toFixed()) <= 1)
        ) {
            return {
                x: Math.max(0, parseInt(maxBounds.x.toFixed())),
                y: Math.max(0, parseInt(maxBounds.y.toFixed())),
                width: Math.min(screenWidth, asset.maxWidth || screenWidth),
                height: Math.min(screenHeight, asset.maxHeight || screenHeight),
            };
        }

        return {
            x: Math.max(0, parseInt(maxBounds.x.toFixed())),
            y: Math.max(0, parseInt(maxBounds.y.toFixed())),
            width: Math.min(screenWidth, parseInt(maxBounds.width.toFixed())),
            height: Math.min(screenHeight, parseInt(maxBounds.height.toFixed())),
        };
    })();
    // if the asset is part of a group, we need to override the y value since approximateMaxBounds doesn't handle it
    // calculate the total height of all siblings
    // then distribute them (starting with the group's y value)
    // adding any extra space evenly divided at top and bottom
    // TODO: move this into the approximateMaxBounds calculation?
    const assetGroup = asset.getParentByType(Config.GROUP_ASSET_TYPE);
    if (assetGroup) {
        const parentBounds = getBounds(assetGroup, screenWidth, screenHeight);
        const totalHeight = parentBounds.height;
        const totalSiblingsHeight = assetGroup.getAssets().reduce((acc, sibling) => {
            return acc + sibling.approximateMaxBounds.height;
        }, 0);
        const heightFactor = totalHeight / totalSiblingsHeight;
        const extraPadding = Math.max(totalHeight - totalSiblingsHeight, 0);
        // calculate the y value based on the asset's index in the group
        let y = parentBounds.y + extraPadding / 2;
        for (const sibling of assetGroup.getAssets()) {
            if (sibling.name === asset.name) {
                break;
            }
            y += sibling.approximateMaxBounds.height * heightFactor;
        }
        bounds.y = Math.max(0, parseInt(y.toFixed()));
    }
    return bounds;
}

export function sortAssets(assets: Asset[], width: number, height: number) {
    function getCentroid(bounds: Bounds) {
        return {
            x: bounds.x + bounds.width / 2,
            y: bounds.y + bounds.height / 2,
        };
    }
    return assets.sort((a, b) => {
        const centroidA = getCentroid(getBounds(a, width, height));
        const centroidB = getCentroid(getBounds(b, width, height));

        if (centroidA.y < centroidB.y) return -1;
        if (centroidA.y > centroidB.y) return 1;

        if (centroidA.x < centroidB.x) return -1;
        if (centroidA.x > centroidB.x) return 1;

        return 0;
    });
}

function isIntersecting(
    a: { x: number; y: number; width: number; height: number },
    b: { x: number; y: number; width: number; height: number },
) {
    return !(
        a.x + a.width <= b.x ||
        b.x + b.width <= a.x ||
        a.y + a.height <= b.y ||
        b.y + b.height <= a.y
    );
}

/**
 * This method checks if a rectangle is fully occluded by other rectangles. We want to determine if an
 * asset is completely covered by other assets, in which case it should not be visible.
 */
function isRectangleFullyOccluded(
    bounds: { x: number; y: number; width: number; height: number },
    occlusionBounds: { x: number; y: number; width: number; height: number }[],
): boolean {
    let remainingBounds = [bounds];

    for (const occlusion of occlusionBounds) {
        const newRemainingBounds: typeof remainingBounds = [];

        for (const remaining of remainingBounds) {
            if (!isIntersecting(remaining, occlusion)) {
                newRemainingBounds.push(remaining);
                continue;
            }

            const left = Math.max(remaining.x, occlusion.x);
            const right = Math.min(remaining.x + remaining.width, occlusion.x + occlusion.width);
            const top = Math.max(remaining.y, occlusion.y);
            const bottom = Math.min(remaining.y + remaining.height, occlusion.y + occlusion.height);

            if (remaining.x < left) {
                newRemainingBounds.push({
                    x: remaining.x,
                    y: remaining.y,
                    width: left - remaining.x,
                    height: remaining.height,
                });
            }

            if (remaining.x + remaining.width > right) {
                newRemainingBounds.push({
                    x: right,
                    y: remaining.y,
                    width: remaining.x + remaining.width - right,
                    height: remaining.height,
                });
            }

            if (remaining.y < top) {
                newRemainingBounds.push({
                    x: left,
                    y: remaining.y,
                    width: right - left,
                    height: top - remaining.y,
                });
            }

            if (remaining.y + remaining.height > bottom) {
                newRemainingBounds.push({
                    x: left,
                    y: bottom,
                    width: right - left,
                    height: remaining.y + remaining.height - bottom,
                });
            }
        }

        remainingBounds = newRemainingBounds;

        if (remainingBounds.length === 0) {
            return true; // Fully occluded
        }
    }

    return false; // Not fully occluded
}

function prepareMarkdownHighlights(asset: TextAsset): string {
    let text = asset.text;

    // Apply highlights from the end to keep the indices valid
    asset.textHighlights
        .sort((a, b) => b[1] - a[1])
        .forEach(([start, end]) => {
            // Slice the text into three parts: before, highlight, after
            const before = text.slice(0, start);
            const highlight = text.slice(start, end);
            const after = text.slice(end);

            // Apply markdown bold syntax to the highlight and reconstruct the string
            // we replace all tabs (\t), newlines (\n), and carriage returns (\r) from the start
            // and end of the highlight. We leave spaces to easily support subword highlights.
            text = before + '**' + highlight.replace(/^[\t\n\r]+|[\t\n\r]+$/g, '') + '**' + after;
        });

    return text;
}

export function getVisibleAssets(
    assets: Asset[],
    screenWidth: number,
    screenHeight: number,
    scene: Slide,
    excludeText: boolean,
): VisibleAssetMetadata[] {
    const occlusionBounds: { x: number; y: number; width: number; height: number }[] = [];
    const visibleAssets: VisibleAssetMetadata[] = [];

    for (const asset of assets) {
        const bounds = getBounds(asset, screenWidth, screenHeight);

        let isVisible: boolean;
        let occludesBounds: boolean;
        let isBackground = false;
        if (Config.isAssetGroup(asset)) {
            isVisible = true; // these are important to the hierarchy
            occludesBounds = false;
        } else if (Config.isTextAsset(asset)) {
            isVisible = true; // always visible
            occludesBounds = false;
        } else if (Config.isMediaAsset(asset) && asset.isEditable === false) {
            isVisible = false;
            occludesBounds = false;
            if (assets.filter((a) => a.isEditable).length < 1) {
                // if this is not editable and there are no other editable assets, we need to treat it as a background
                isVisible = true;
                occludesBounds = true;
                isBackground = true;
            }
        } else {
            const isHidden = isRectangleFullyOccluded(bounds, occlusionBounds);

            if (!isHidden) {
                if (Config.isMediaAsset(asset)) {
                    isVisible = true;
                    occludesBounds = true; // theoretically could have some transparency but we'll ignore that for now
                } else if (Config.isTextAsset(asset)) {
                    isVisible = true;
                    occludesBounds = false;
                } else if (Config.isRectangleAsset(asset)) {
                    isVisible = true;
                    if (asset.mask !== null) {
                        // TODO: compute dimensions based on the mask
                        // until we know where the asset is, assume it doesn't occlude
                        occludesBounds = false;
                    } else {
                        const alpha = asset.fillAlpha;
                        if (alpha < 1) {
                            occludesBounds = false;
                        } else {
                            occludesBounds = true;
                        }
                    }
                } else if (Config.isColorAsset(asset)) {
                    isVisible = true;
                    if (asset.mask !== null) {
                        // TODO: compute dimensions based on the mask
                        // until we know where the colour asset is, assume it doesn't occlude
                        occludesBounds = false;
                    } else {
                        const alpha = asset.sourceAlpha;
                        if (alpha < 1) {
                            occludesBounds = false;
                        } else {
                            occludesBounds = true;
                        }
                    }
                } else if (Config.isCircleAsset(asset)) {
                    // TODO: circle are used extremely creatively, often as masks, and often leveraging large stroke widths
                    // Because of this, we are not able to accurately determine occlusion
                    isVisible = true;
                    occludesBounds = false;
                } else if (Config.isGradientAsset(asset)) {
                    isVisible = true;
                    occludesBounds = false; // many are transparent, etc
                } else if (Config.isLottieAsset(asset)) {
                    isVisible = false;
                    occludesBounds = false;
                    if (assets.filter((a) => a.isEditable).length < 1) {
                        // if this is not editable and there are no other editable assets, we need to treat it as a background
                        isVisible = true;
                        occludesBounds = true;
                        isBackground = true;
                    }
                } else {
                    isVisible = true;
                    occludesBounds = false; // TODO – look into cases here?
                }
            }
        }

        if (occludesBounds) {
            occlusionBounds.push(bounds);
        }

        const attributes: { [k: string]: unknown } = {};
        attributes.isEditable = asset.isEditable;
        attributes.linkToSceneGroup = Config.isMediaAsset(asset) && asset.linkToSceneGroup;
        attributes.isBackground = isBackground;
        if (Config.isAnyTextAsset(asset) && asset.text) {
            if (excludeText) {
                attributes.text = ''; // Remove text content if we're excluding text
            } else {
                attributes.text = scene
                    .getAssetsByName(asset.name)
                    .map(prepareMarkdownHighlights)
                    .join(SUBSCENE_SEPARATOR);
            }
        }
        if (Config.isAnyTextAsset(asset) && Number.isFinite(asset.textSize)) {
            attributes.textSize = asset.textSize;
        }
        if (Config.isColorAsset(asset) && asset.sourceColor) {
            attributes.color = asset.sourceColor;
        }
        if (Config.isRectangleAsset(asset) && asset.color) {
            attributes.color = asset.color;
        }
        if (Config.isColorAsset(asset) && asset.sourceAlpha) {
            attributes.alpha = asset.sourceAlpha;
        }
        if (Config.isMediaAsset(asset) && asset.sourceData.uuid) {
            attributes.uuid = asset.sourceData.uuid;
        }
        // If the asset is visible, add it to the visible assets list
        if (isVisible) {
            visibleAssets.push({
                name: asset.name,
                classType: asset.classType,
                bounds,
                attributes,
            });
        } else if (!Config.isColorAsset(asset)) {
            console.log('non-visible asset (expected?):', asset.name, bounds, attributes);
        }
    }

    return visibleAssets;
}

/*
The following section is concerned with building a tree structure that represents the visual hierarchy of the scene
*/
const makeNode = (asset: VisibleAssetMetadata, children: Node[] = []) => {
    return {
        asset,
        children,
    };
};

const makeRoot = (children: Node[], width: number, height: number, sceneDesign: string): Node => {
    return makeNode(
        {
            name: sceneDesign,
            bounds: { x: 0, y: 0, width: width, height: height },
            classType: 'Scene',
            attributes: {},
        },
        children || [],
    );
};

function area(bounds: Bounds): number {
    return bounds.width * bounds.height;
}

function isContained(inner: VisibleAssetMetadata, outer: VisibleAssetMetadata) {
    return (
        inner.bounds.x >= outer.bounds.x &&
        inner.bounds.y >= outer.bounds.y &&
        inner.bounds.x + inner.bounds.width <= outer.bounds.x + outer.bounds.width &&
        inner.bounds.y + inner.bounds.height <= outer.bounds.y + outer.bounds.height
    );
}

function insertNode(currentNode: Node, newNode: Node) {
    if (!currentNode.children) {
        currentNode.children = [];
    }

    // Check if box fits inside any of the children
    for (const child of currentNode.children) {
        if (isContained(newNode.asset, child.asset)) {
            insertNode(child, newNode);
            return;
        }
    }

    // If no children can contain the box, add it as a child
    currentNode.children.push(newNode);
}

function buildTree(nodes: Node[], width: number, height: number, sceneDesign: string): Node[] {
    const root = makeRoot([], width, height, sceneDesign);

    nodes.forEach((node) => {
        insertNode(root, node);
    });

    return root.children;
}

/*
The following section is concerned with formatting the tree structure into the SceneAnnotation string format
*/

function sortNodes(nodes: Node[], width: number): Node[] {
    // bucket into 4 columns left to right
    const buckets: Node[][] = [];

    // Define a threshold to consider boxes as part of the same column
    const threshold = width / 4; // You can adjust this threshold based on your specific data

    // Sorting boxes by the x coordinate to determine potential column breaks
    nodes
        .sort((a, b) => a.asset.bounds.x - b.asset.bounds.x)
        .forEach((node) => {
            const bucketKey = Math.floor(node.asset.bounds.x / threshold);
            if (!buckets[bucketKey]) {
                buckets[bucketKey] = [];
            }
            buckets[bucketKey].push(node);
        });

    Object.keys(buckets).forEach((key) => {
        if (typeof key === 'number') {
            // Sort the boxes in each column by the y coordinate
            buckets[key] = sortNodesVertically(buckets[key]);
        }
    });

    return buckets.flat();
}

function sortNodesVertically(nodes: Node[]) {
    return nodes.sort((a, b) => {
        if (a.asset.bounds.y !== b.asset.bounds.y) {
            return a.asset.bounds.y - b.asset.bounds.y;
        } else {
            return a.asset.bounds.x - b.asset.bounds.x;
        }
    });
}

function drawToBase64Image(width: number, height: number, tree: Node): string {
    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // get a list of all assets in the tree (recurisvely)
    const assets: VisibleAssetMetadata[] = [];
    function getAssets(node: Node) {
        assets.push(node.asset);
        node.children.forEach((child) => getAssets(child));
    }
    getAssets(tree);

    // skip the first "scene design" node
    assets.shift();

    // for each visible asset, draw it on the canvas in a different color (stroke only)
    assets.forEach((asset, i) => {
        ctx.strokeStyle = `hsl(${(i * 137.508) % 360}, 100%, 50%)`;
        ctx.fillStyle = `hsla(${(i * 137.508) % 360}, 100%, 50%, 0.2)`; // 20% opacity
        ctx.lineWidth = 5;
        ctx.strokeRect(asset.bounds.x, asset.bounds.y, asset.bounds.width, asset.bounds.height);
        // only fill rect if not full size
        if (asset.bounds.width < width || asset.bounds.height < height) {
            ctx.fillRect(asset.bounds.x, asset.bounds.y, asset.bounds.width, asset.bounds.height);
        }
        // write the asset name at the top left corner of the asset
        ctx.fillStyle = ctx.strokeStyle;
        ctx.font = '30px Arial';
        // very light version of the fill style for background behind the text (50% opacity)
        ctx.fillStyle = `hsla(${(i * 137.508) % 360}, 100%, 50%, 0.5)`;
        ctx.fillRect(
            asset.bounds.x + 5,
            asset.bounds.y,
            ctx.measureText(asset.name).width + 10,
            50,
        );
        ctx.fillStyle = 'black';
        ctx.fillText(asset.name, asset.bounds.x + 10, asset.bounds.y + 30);
    });

    // Convert canvas to base64 image
    return canvas.toDataURL('image/png');
}

export function fmt(
    node: Node,
    width: number,
    renderText: boolean,
    mediaMap: { [k: string]: string },
    indent: string = '',
): string {
    let assetTypeName: string;
    if (
        (node.asset.classType === Config.MEDIA_ASSET_TYPE ||
            node.asset.classType === Config.LOTTIE_ASSET_TYPE) &&
        node.asset.attributes?.isBackground
    ) {
        assetTypeName = 'BACKGROUND';
    } else {
        const nameMap: Record<string, string> = {
            Scene: 'SCENE',
            [Config.GROUP_ASSET_TYPE]: 'VERTICAL_LIST',
            [Config.ICON_ASSET_TYPE]: 'PICTOGRAM',
            [Config.COLOR_ASSET_TYPE]: 'BACKGROUND',
            [Config.CIRCLE_ASSET_TYPE]: 'CIRCLE',
            [Config.GRADIENT_ASSET_TYPE]: 'GRADIENT',
            [Config.RECTANGLE_ASSET_TYPE]: 'BACKGROUND',
            [Config.MEDIA_ASSET_TYPE]: 'MEDIA',
            [Config.TEXT_ASSET_TYPE]: 'TEXT',
            [Config.FANCY_CAPTION_ASSET_TYPE]: 'ANIMATED_CAPTIONS',
        };
        assetTypeName = nameMap[node.asset.classType];
    }
    const name = assetTypeName;
    if (!name) {
        throw new Error(`Unknown asset type: ${node.asset.classType}`);
    }
    const editableAssetTypes: string[] = [
        // we only want to mark these assets as editable
        Config.TEXT_ASSET_TYPE,
        Config.TEXT_ASSET_TYPE,
        Config.MEDIA_ASSET_TYPE,
        Config.ICON_ASSET_TYPE,
    ];
    // Base part for any scene or sub-scene
    let assetName = node.asset.name ?? '';
    if (!editableAssetTypes.includes(node.asset.classType) && node.asset.classType !== 'Scene') {
        assetName = '';
    }
    const linkToSceneGroup = node.asset.attributes?.linkToSceneGroup ?? false;
    const editable =
        node.asset.attributes?.isEditable &&
        editableAssetTypes.includes(node.asset.classType) &&
        !linkToSceneGroup
            ? 'EDITABLE'
            : '';
    let textSize = '';
    if (node.asset.attributes?.textSize) {
        textSize = 'size=' + node.asset.attributes.textSize.toString();

        // augment with max chars
        const height = node.asset.bounds.height;
        const numLines = Math.max(
            Math.floor(height / (node.asset.attributes.textSize as number)),
            1,
        );
        const width = node.asset.bounds.width;
        const charsPerLine = Math.max(
            Math.floor(width / ((node.asset.attributes.textSize as number) * 0.5)),
            1,
        ); // 0.5 is a rough estimate of the average width of a character
        textSize += `&max_chars=${numLines * charsPerLine}`;
    }
    delete node.asset.attributes?.isEditable;
    delete node.asset.attributes?.alpha;
    delete node.asset.attributes?.linkToSceneGroup;
    delete node.asset.attributes?.textSize;
    delete node.asset.attributes?.isBackground;

    let description = '';
    if (renderText) {
        if (node.asset.attributes?.text) {
            if (typeof node.asset.attributes.text === 'string') {
                // replace all whitespace characters (newlines, tabs, non-breaking spaces, etc) with a single space
                // TODO: might want to preserve some of these in the future?
                description = node.asset.attributes.text.trim().replace(/(\r\n|\n|\r)/g, '\\n');
                if (description.split(SUBSCENE_SEPARATOR).every((x) => x === LOREM_IPSUM)) {
                    description = ' '; // specifically an empty string with width
                }
            }
        }
    }

    if (node.asset.attributes?.color) {
        const color = node.asset.attributes.color;
        if (typeof color === 'number') {
            // TODO: treating all backgrounds as solid color now. In future, can render patch of the background image
            // and use GPT-4 to describe the image and use that here.
            description = `Solid #${color.toString(16).padStart(6, '0')}`;
        }
    }

    if (name === 'ANIMATED_CAPTIONS') {
        description = 'Fancy captions that animate word by word what the speaker is saying';
    }

    if (linkToSceneGroup) {
        description = 'Talking head footage of the speaker';
    }

    if (node.asset.classType === Config.MEDIA_ASSET_TYPE && name === 'BACKGROUND') {
        assetName = '';
        description = '';
    }

    const boundsString = `${node.asset.bounds.x},${node.asset.bounds.y},${node.asset.bounds.width},${node.asset.bounds.height}`;
    const row = [name, assetName, description, boundsString, textSize, editable].filter(
        (x) => ![null, undefined, ''].includes(x),
    );

    let result = `${indent}${row.join(' ')}`;

    // Check if there are nested elements
    if (node.children && node.children.length > 0) {
        result += ' (\n'; // Start the nested block

        // Process each nested element
        if (node.asset.classType === Config.GROUP_ASSET_TYPE) {
            node.children.forEach((child) => {
                result += fmt(child, width, renderText, mediaMap, indent + '    ') + '\n'; // Recursive call with increased indent
            });
        } else {
            sortNodes(node.children, width).forEach((child) => {
                result += fmt(child, width, renderText, mediaMap, indent + '    ') + '\n'; // Recursive call with increased indent
            });
        }
        result += `${indent})`; // Close the nested block
    }

    return result;
}

// Process subscene assets recursively since groups can contain other groups
function processSubsceneAsset(
    node: Node,
    scene: Slide,
    width: number,
    height: number,
    visibleAssetNames: string[],
    visibleAssets: VisibleAssetMetadata[],
) {
    const asset = scene.getAssets().find((x) => x.name === node.asset.name);
    const subAssets = sortAssets(asset.getAssets({ deep: false }), width, height).filter((x) =>
        visibleAssetNames.includes(x.name),
    );

    if (subAssets.length === 1 && subAssets[0].classType !== Config.GROUP_ASSET_TYPE) {
        node.asset = visibleAssets.find((x) => x.name === subAssets[0].name);
    } else {
        node.children = []; // Ensure children array is initialized
        for (const subAsset of subAssets) {
            const childNode = makeNode(visibleAssets.find((x) => x.name === subAsset.name));
            processSubsceneAsset(childNode, scene, width, height, visibleAssetNames, visibleAssets); // Recursive call
            node.children.push(childNode);
        }
    }
}

type SceneAnnotationSettings = {
    scene: Slide;
    excludeText?: boolean; // optional flag for excluding text content from text assets in scene annotation
};

export class SceneAnnotation {
    #scene: Slide;
    #excludeText: boolean;

    constructor(settings: SceneAnnotationSettings) {
        const { scene } = settings;
        this.#scene = scene;
        this.#excludeText = settings.excludeText;
    }

    #getPrunedTree() {
        const scene = this.#scene;
        // should always be in 1080p
        const width = scene.root.referenceResolution.width;
        const height = scene.root.referenceResolution.height;

        // Find all visible assets in the scene
        const editableTextAssets = scene
            .getAssetsByType(Config.TEXT_ASSET_TYPE)
            .filter((x) => x.isEditable);
        for (const asset of editableTextAssets) {
            if (!asset.text.trim()) {
                asset.text = LOREM_IPSUM;
            }
        }

        // get the visible assets in the scene
        /**
         * remove "embedded logo", "watermark" assets as they're distracting
         * we want to ignore them for now
         * also remove attribution text assets
         */
        const sceneAssets = scene.scene.getAssets();
        const subsceneAssets = scene.subscenes[0].getAssets();
        const assets = sceneAssets
            .concat(subsceneAssets)
            .filter((x) => x.name.indexOf('embedded_logo') === -1)
            .filter((x) => x.name.indexOf('watermark') === -1)
            // for now exclude stickers since they appear as media assets in the system
            .filter((x) => Config.isStickerAsset(x) === false)
            // exclude waveform assets
            .filter((x) => {
                if (Config.isIconAsset(x) && x.playbackMode === Config.PLAYBACK_WAVEFORM) {
                    return false;
                }
                return true;
            });

        assets.reverse(); // reverse the assets so that the last asset is the topmost one
        const visibleAssets = getVisibleAssets(assets, width, height, scene, this.#excludeText);
        const visibleAssetNames = visibleAssets.map((asset) => asset.name);

        // Filter the scene assets to only include the visible ones and apply some pre-sorting as well
        const sceneAssetNames = scene.getAssets({ deep: false }).map((x) => x.name);
        const visibleSceneAssets = visibleAssets
            .filter((x) => sceneAssetNames.find((y) => y === x.name))
            .map((x, i) => {
                x['i'] = i;
                return x;
            })
            .sort((a, b) => {
                if (a.i !== b.i) {
                    return area(b.bounds) - area(a.bounds);
                } else {
                    return a.i - b.i;
                }
            })
            .filter((x) => {
                // TODO: we might not get any visible assets due to alpha rules here
                if (x.attributes.alpha) return (x.attributes.alpha as number) >= 1; // ignore transparent assets for now
                // Include Lottie assets that are treated as background, exclude others
                if (x.classType === 'LottieAsset') return x.attributes.isBackground === true;
                return true;
            })
            .map((a) => makeNode(a));

        const subsceneAssetNames = scene.subscenes[0].getAssets({ deep: false }).map((x) => x.name);
        const visibleSubsceneAssets = visibleAssets
            .filter((x) => subsceneAssetNames.find((y) => y === x.name))
            .map((x, i) => {
                x['i'] = i;
                return x;
            })
            .sort((a, b) => {
                if (a.i !== b.i) {
                    return area(b.bounds) - area(a.bounds);
                } else {
                    return a.i - b.i;
                }
            })
            .filter((x) => {
                if (x.attributes.alpha) return (x.attributes.alpha as number) >= 1; // ignore transparent assets for now
                // Include Lottie assets that are treated as background, exclude others
                if (x.classType === 'LottieAsset') return x.attributes.isBackground === true;
                return true;
            })
            .map((a) => makeNode(a));

        visibleSceneAssets.forEach((node) => {
            const asset = scene.getAssets().find((x) => x.name === node.asset.name);
            const subAssets = sortAssets(asset.getAssets({ deep: false }), width, height).filter(
                (x) => visibleAssetNames.includes(x.name),
            );
            if (subAssets.length === 1) {
                node.asset = visibleAssets.find((x) => x.name === subAssets[0].name);
            } else {
                for (const subAsset of sortAssets(
                    asset.getAssets({ deep: false }),
                    width,
                    height,
                )) {
                    if (visibleAssetNames.includes(subAsset.name)) {
                        node.children.push(
                            makeNode(visibleAssets.find((x) => x.name === subAsset.name)),
                        );
                    }
                }
            }
        });

        visibleSubsceneAssets.forEach((node) => {
            processSubsceneAsset(node, scene, width, height, visibleAssetNames, visibleAssets);
        });

        for (const asset of editableTextAssets) {
            if (asset.text.split(SUBSCENE_SEPARATOR).every((x) => x === LOREM_IPSUM)) {
                asset.text = ' '; // specifically an empty string with width
            }
        }

        const allAssets = visibleSceneAssets.concat(visibleSubsceneAssets);

        const sceneDesign = scene.sceneDesign;

        // build the visual hierarchy tree
        const tree = makeRoot(
            buildTree(allAssets, width, height, sceneDesign),
            width,
            height,
            sceneDesign,
        );

        function pruneGroupLeafNodes(node: Node): Node | null {
            // Recursively traverse the children to handle deep leaf nodes first
            node.children = node.children
                .map((child) => pruneGroupLeafNodes(child))
                .filter((child) => child !== null);

            // Determine if the current node is a leaf node
            const isLeaf = node.children.length === 0;

            if (isLeaf && node.asset.classType === Config.GROUP_ASSET_TYPE) {
                return null;
            }

            return node;
        }
        const prunedTree = pruneGroupLeafNodes(tree);
        return prunedTree;
    }

    getImage() {
        const prunedTree = this.#getPrunedTree();
        return drawToBase64Image(
            this.#scene.root.referenceResolution.width,
            this.#scene.root.referenceResolution.height,
            prunedTree,
        );
    }

    getJSON() {
        const prunedTree = this.#getPrunedTree();
        return JSON.stringify(
            {
                prunedTree,
            },
            null,
            2,
        );
    }

    getText({ renderText = true, renderCaptions = false, mediaMap = {} } = {}) {
        const prunedTree = this.#getPrunedTree();
        const width = this.#scene.root.referenceResolution.width;
        const sceneAnnotation = fmt(prunedTree, width, renderText, mediaMap, '');
        const isVoiceDriven =
            this.#scene?.sceneGroup?.mediaAsset !== undefined ||
            this.#scene?.sceneGroup?.voiceoverAsset !== undefined;
        if (renderCaptions && isVoiceDriven) {
            const captionText = this.#scene.extractTranscript().text;
            return `Captions: ${captionText}\n${sceneAnnotation}\n`;
        }
        return sceneAnnotation;
    }
}
